// @flow

import * as React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useLocation } from 'react-router-dom';

import {
  selectCartCurrency,
  selectCartTotal,
  selectUnusedDiscounts,
} from '@gandi/cart';

import { useTranslator } from '@gandi/translate';
import { Base, IconButton } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import AddDiscount from './AddDiscount';
import { DiscountRows } from './DiscountRows';
import { PromoBadge } from './PromoBadge';

import { TotalArticles } from './TotalArticles';
import TotalRow from './TotalRow';
import TotalTaxRow from './TotalTaxRow';

import styles from './Total.scss';

type Props = {
  handleConfirmationRemove: React.ReactSetStateFunction<boolean>,
};

export function Total({
  handleConfirmationRemove,
}: Props): null | React.MixedElement {
  const { __ } = useTranslator();
  const currency = useAppSelector(selectCartCurrency);
  const total = useAppSelector(selectCartTotal);

  const unusedDiscounts = useAppSelector(selectUnusedDiscounts);

  const [success, setSuccess] = React.useState<?string>();

  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');
  const accountChoicePage = location.pathname.includes(
    '/configure/account-choice',
  );

  const { FF_SHOW_SAVINGS } = useFeatureFlags();

  if (isEmpty(total) || !currency) return null;
  const { taxes, afterTaxes, beforeTaxes } = total;

  const actualTaxes = Array.isArray(taxes)
    ? taxes.filter(tax => tax.name !== 'notax')
    : [];

  const hasDiscounts = !!(total.discounts?.length || unusedDiscounts?.length);

  return (
    <div className={styles.container}>
      <Base flex flexJustifyBetween flexAlignItemsCenter>
        <h3>{__('Cart Summary')}</h3>
        {overviewPage && (
          <IconButton
            icon="trash"
            kind="ghost"
            className={styles.removeButton}
            onClick={handleConfirmationRemove}
          >
            {__('Remove all products')}
          </IconButton>
        )}
      </Base>
      <TotalArticles beforeTaxes={beforeTaxes} />

      {FF_SHOW_SAVINGS && <PromoBadge />}

      <AddDiscount success={success} setSuccess={setSuccess} />
      {hasDiscounts && <DiscountRows setSuccess={setSuccess} />}
      {actualTaxes.length > 0 &&
        actualTaxes.map(tax => (
          <TotalTaxRow key={tax.name} tax={tax} currency={currency} />
        ))}
      <TotalRow
        amount={afterTaxes}
        currency={currency}
        info={accountChoicePage}
      />
    </div>
  );
}
